import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ShieldCheck,
  Server,
  Layers,
  FlaskConical,
  Coins,
  ScrollText,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Lock,
  Sparkles,
  Clapperboard,
  Cpu,
  Info,
  Users,
} from "lucide-react";
import { useAuth } from "@/store/AuthContext";
import { api } from "@/lib/api";
import { t } from "@/i18n";
import LLMProvidersList from "@/components/admin/LLMProvidersList";
import LLMProviderModal from "@/components/admin/LLMProviderModal";
import LLMModelsConfig from "@/components/admin/LLMModelsConfig";
import InvitationsManager from "@/components/admin/InvitationsManager";

export const isUserOwner = (user) => Boolean(user && (user.id === "owner" || user._id === "owner" || user.role === "owner"));

const TAB_ICONS = {
  overview: Server,
  users: Users,
  connections: Layers,
  models: Cpu,
  lab: FlaskConical,
  budget: Coins,
  logs: ScrollText,
};

const TAB_KEYS = ["overview", "users", "connections", "models", "lab", "budget", "logs"];

const PendingSectionBanner = ({ message }) => (
  <div
    data-testid="admin-pending-notice"
    className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-xs leading-relaxed text-amber-300"
  >
    <Info size={16} className="mt-0.5 shrink-0 text-amber-400" />
    <div className="space-y-1">
      <div className="font-semibold text-amber-400">{t.admin.pendingTag}</div>
      <p className="text-[#a3a3a3]">{message}</p>
    </div>
  </div>
);

const AdminPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "overview";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [backendStatus, setBackendStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // Estado para proveedores LLM (Etapa 2A.2)
  const [providers, setProviders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [testingProviderId, setTestingProviderId] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedModelProvId, setSelectedModelProvId] = useState(null);

  const isOwner = isUserOwner(user);

  const loadLLMData = async () => {
    try {
      setLoadingProviders(true);
      const [provsRes, tmplsRes] = await Promise.all([
        api.getLLMProviders().catch(() => []),
        api.getLLMTemplates().catch(() => []),
      ]);
      setProviders(provsRes || []);
      setTemplates(tmplsRes || []);
      if (provsRes && provsRes.length > 0 && !selectedModelProvId) {
        setSelectedModelProvId(provsRes[0].id);
      }
    } catch {
      // Ignorar errores silenciosos en carga inicial
    } finally {
      setLoadingProviders(false);
    }
  };

  useEffect(() => {
    let active = true;
    if (isOwner) {
      api
        .getAdminStatus()
        .then((res) => {
          if (active) {
            setBackendStatus(res);
            setStatusLoading(false);
          }
        })
        .catch(() => {
          if (active) setStatusLoading(false);
        });

      loadLLMData();
    } else {
      setStatusLoading(false);
    }
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  const handleCreateOrUpdateProvider = async (payload) => {
    try {
      setModalLoading(true);
      if (editingProvider) {
        await api.updateLLMProvider(editingProvider.id, payload);
      } else {
        await api.createLLMProvider(payload);
      }
      setProviderModalOpen(false);
      setEditingProvider(null);
      await loadLLMData();
    } catch (err) {
      alert(err?.message || "Error al guardar el proveedor LLM");
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteProvider = async (id) => {
    try {
      await api.deleteLLMProvider(id);
      await loadLLMData();
    } catch (err) {
      alert(err?.message || "Error al eliminar el proveedor");
    }
  };

  const handleTestCredentials = async (id) => {
    setTestingProviderId(id);
    try {
      const res = await api.testLLMProvider(id, { test_type: "credentials" });
      await loadLLMData();
      return res;
    } finally {
      setTestingProviderId(null);
    }
  };

  const handleTestInference = async (id, payload) => {
    setTestingProviderId(id);
    try {
      const res = await api.testLLMProvider(id, payload);
      await loadLLMData();
      return res;
    } finally {
      setTestingProviderId(null);
    }
  };

  const handleSaveModelsForProvider = async (provId, newModels) => {
    try {
      await api.updateLLMProvider(provId, { models: newModels });
      await loadLLMData();
    } catch (err) {
      alert(err?.message || "Error al actualizar modelos");
    }
  };

  if (!isOwner) {
    return (
      <div
        data-testid="admin-unauthorized-view"
        className="flex min-h-[calc(100vh-4rem)] flex-1 flex-col items-center justify-center p-6 text-center"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-red-500/30 bg-red-500/10 text-red-400 mb-4">
          <Lock size={26} />
        </div>
        <h1 className="font-display text-xl font-semibold text-[#ededed]">
          {t.admin.unauthorizedTitle}
        </h1>
        <p className="mt-2 max-w-md text-xs leading-relaxed text-[#8a8a8a]">
          {t.admin.unauthorizedBody}
        </p>
        <a
          href="/"
          data-testid="admin-unauthorized-back-btn"
          className="v-btn-primary v-btn-sm mt-6 inline-flex items-center gap-2"
        >
          <ArrowLeft size={14} />
          <span>{t.admin.returnHome}</span>
        </a>
      </div>
    );
  }

  return (
    <div data-testid="admin-page-container" className="flex flex-1 flex-col overflow-y-auto bg-[#101010] p-6 lg:p-8">
      {/* 1. Header con identidad de Owner y badge */}
      <div className="flex flex-col gap-3 pb-6 border-b border-[#262626] sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#f5b544]/30 bg-[#f5b544]/10 text-[#f5b544]">
            <ShieldCheck size={22} />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="font-display text-lg font-bold tracking-tight text-[#ededed]">
                {t.admin.title}
              </h1>
              <span
                data-testid="admin-owner-badge"
                className="inline-flex items-center gap-1 rounded bg-[#f5b544]/15 px-2 py-0.5 text-[10px] font-semibold text-[#f5b544] border border-[#f5b544]/30"
              >
                <CheckCircle2 size={11} />
                {t.admin.ownerBadge}
              </span>
            </div>
            <p className="text-xs text-[#8a8a8a]">{t.admin.subtitle}</p>
          </div>
        </div>

        {/* Verificación de handshake con backend */}
        <div className="flex items-center gap-2 rounded-lg border border-[#2a2a2a] bg-[#161616] px-3 py-1.5 text-xs text-[#a3a3a3]">
          <span className="h-2 w-2 rounded-full bg-emerald-400" />
          <span className="font-mono text-[11px]">
            {user?.name || "Owner"} · {user?.email || "owner@veyra"}
          </span>
        </div>
      </div>

      {/* 2. Barra de Navegación por Tabs de las 6 Secciones */}
      <div className="mt-6 border-b border-[#262626]">
        <nav className="flex space-x-1 overflow-x-auto no-scrollbar" aria-label="Secciones Administrativas">
          {TAB_KEYS.map((key) => {
            const Icon = TAB_ICONS[key];
            const isActive = activeTab === key;
            const label = t.admin.tabs[key] || key;
            return (
              <button
                key={key}
                type="button"
                data-testid={`admin-tab-${key}`}
                onClick={() => setActiveTab(key)}
                className={`group inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-xs font-medium transition-all ${
                  isActive
                    ? "border-[#f5b544] text-[#f5b544]"
                    : "border-transparent text-[#7a7a7a] hover:border-[#383838] hover:text-[#d4d4d4]"
                }`}
              >
                <Icon size={14} className={isActive ? "text-[#f5b544]" : "text-[#6b6b6b] group-hover:text-[#a3a3a3]"} />
                <span>{label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* 3. Contenido de las Secciones */}
      <div className="mt-6 flex-1">
        {/* SECCIÓN A: Resumen */}
        {activeTab === "overview" && (
          <div data-testid="admin-section-overview" className="flex flex-col gap-6">
            <div>
              <h2 className="text-sm font-semibold text-[#ededed]">
                {t.admin.sections.overview.title}
              </h2>
              <p className="mt-0.5 text-xs text-[#8a8a8a]">
                {t.admin.sections.overview.desc}
              </p>
            </div>

            {/* Sesión Administrativa Confirmada */}
            <div className="rounded-xl border border-[#262626] bg-[#161616] p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={16} className="text-emerald-400" />
                  <span className="text-xs font-semibold text-[#ededed]">
                    {t.admin.sections.overview.sessionTitle}
                  </span>
                </div>
                <span className="font-mono text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                  200 OK · Autorizado
                </span>
              </div>
              <p className="mt-2 text-xs text-[#8a8a8a]">
                {t.admin.sections.overview.sessionDesc}
              </p>
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 text-[11px] font-mono text-[#a3a3a3] pt-3 border-t border-[#222]">
                <div>Usuario: <span className="text-[#ededed]">{user?.name}</span></div>
                <div>Correo: <span className="text-[#ededed]">{user?.email}</span></div>
              </div>
            </div>

            {/* Separación de Dominios de IA */}
            <div>
              <div className="v-eyebrow mb-2 text-[#737373]">
                {t.admin.sections.overview.domainsTitle}
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Dominio 1 */}
                <div className="flex flex-col justify-between rounded-xl border border-[#2a2a2a] bg-[#141414] p-4">
                  <div>
                    <div className="flex items-center gap-2 text-[#f5b544]">
                      <Sparkles size={16} />
                      <h3 className="text-xs font-semibold text-[#ededed]">
                        {t.admin.sections.overview.cdDomainTitle}
                      </h3>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-[#8a8a8a]">
                      {t.admin.sections.overview.cdDomainDesc}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#222] flex items-center justify-between text-[11px] text-[#737373]">
                    <span>Tráfico: Tokens LLM</span>
                    <span className="font-mono text-amber-400/80">Etapa 2 y 3</span>
                  </div>
                </div>

                {/* Dominio 2 */}
                <div className="flex flex-col justify-between rounded-xl border border-[#2a2a2a] bg-[#141414] p-4">
                  <div>
                    <div className="flex items-center gap-2 text-indigo-400">
                      <Clapperboard size={16} />
                      <h3 className="text-xs font-semibold text-[#ededed]">
                        {t.admin.sections.overview.genDomainTitle}
                      </h3>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-[#8a8a8a]">
                      {t.admin.sections.overview.genDomainDesc}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#222] flex items-center justify-between text-[11px] text-[#737373]">
                    <span>Tráfico: Binarios Imagen/Vídeo</span>
                    <span className="font-mono text-emerald-400">APIMart Operativo</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SECCIÓN USUARIOS E INVITACIONES */}
        {activeTab === "users" && (
          <div data-testid="admin-section-users" className="flex flex-col gap-6">
            <InvitationsManager />
          </div>
        )}

        {/* SECCIÓN B: APIs y Conexiones */}
        {activeTab === "connections" && (
          <div data-testid="admin-section-connections" className="flex flex-col gap-6">
            <div>
              <h2 className="text-sm font-semibold text-[#ededed]">
                {t.admin.sections.connections.title}
              </h2>
              <p className="mt-0.5 text-xs text-[#8a8a8a]">
                {t.admin.sections.connections.desc}
              </p>
            </div>

            <LLMProvidersList
              providers={providers}
              loading={loadingProviders}
              onRefresh={loadLLMData}
              onOpenCreate={() => {
                setEditingProvider(null);
                setProviderModalOpen(true);
              }}
              onOpenEdit={(prov) => {
                setEditingProvider(prov);
                setProviderModalOpen(true);
              }}
              onDelete={handleDeleteProvider}
              onTestCredentials={handleTestCredentials}
              onTestInference={handleTestInference}
              testingProviderId={testingProviderId}
            />
          </div>
        )}

        {/* SECCIÓN C: Modelos */}
        {activeTab === "models" && (
          <div data-testid="admin-section-models" className="flex flex-col gap-6">
            <div>
              <h2 className="text-sm font-semibold text-[#ededed]">
                {t.admin.sections.models.title}
              </h2>
              <p className="mt-0.5 text-xs text-[#8a8a8a]">
                {t.admin.sections.models.desc}
              </p>
            </div>

            {providers.length === 0 ? (
              <div
                data-testid="admin-models-no-providers"
                className="rounded-2xl border border-dashed border-[#2a2a2a] bg-[#141414] p-8 text-center"
              >
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-[#2a2a2a] bg-[#1a1a1a] text-[#8a8a8a] mb-3">
                  <Cpu size={22} />
                </div>
                <h3 className="font-display text-sm font-semibold text-[#ededed]">
                  No hay proveedores configurados
                </h3>
                <p className="mt-1 text-xs text-[#8a8a8a] max-w-md mx-auto leading-relaxed">
                  Para configurar modelos dinámicos y capacidades, registre primero un proveedor en la pestaña de Conexiones.
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab("connections")}
                  className="v-btn-primary v-btn-sm mt-4"
                >
                  Ir a Conexiones
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {/* Selector de proveedor para gestionar sus modelos */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-[#262626] bg-[#161616] p-4">
                  <div className="flex items-center gap-2.5">
                    <span className="text-xs font-semibold text-[#ededed]">Proveedor:</span>
                    <select
                      data-testid="admin-models-provider-select"
                      value={selectedModelProvId || (providers[0] ? providers[0].id : "")}
                      onChange={(e) => setSelectedModelProvId(e.target.value)}
                      className="v-input text-xs font-medium"
                    >
                      {providers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label} ({p.provider_type})
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className="text-[11px] text-[#8a8a8a]">
                    Modelos configurados: {(providers.find((p) => p.id === (selectedModelProvId || providers[0]?.id))?.models || []).length}
                  </span>
                </div>

                {/* Editor dinámico de modelos */}
                {(() => {
                  const activeProv = providers.find((p) => p.id === (selectedModelProvId || providers[0]?.id));
                  if (!activeProv) return null;

                  return (
                    <div className="rounded-2xl border border-[#262626] bg-[#141414] p-5">
                      <LLMModelsConfig
                        models={activeProv.models || []}
                        onChange={(newModels) => handleSaveModelsForProvider(activeProv.id, newModels)}
                      />
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* SECCIÓN D: Laboratorio IA */}
        {activeTab === "lab" && (
          <div data-testid="admin-section-lab" className="flex flex-col gap-6">
            <div>
              <h2 className="text-sm font-semibold text-[#ededed]">
                {t.admin.sections.lab.title}
              </h2>
              <p className="mt-0.5 text-xs text-[#8a8a8a]">
                {t.admin.sections.lab.desc}
              </p>
            </div>

            <PendingSectionBanner message={t.admin.sections.lab.pendingNotice} />
          </div>
        )}

        {/* SECCIÓN E: Costes y Presupuestos */}
        {activeTab === "budget" && (
          <div data-testid="admin-section-budget" className="flex flex-col gap-6">
            <div>
              <h2 className="text-sm font-semibold text-[#ededed]">
                {t.admin.sections.budget.title}
              </h2>
              <p className="mt-0.5 text-xs text-[#8a8a8a]">
                {t.admin.sections.budget.desc}
              </p>
            </div>

            <PendingSectionBanner message={t.admin.sections.budget.pendingNotice} />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-[#262626] bg-[#141414] p-3.5">
                <span className="text-[10px] uppercase font-mono text-[#737373]">Rubro 1</span>
                <div className="mt-1 font-semibold text-xs text-[#ededed]">
                  {t.admin.sections.budget.bucket1}
                </div>
                <div className="mt-3 text-[11px] text-[#8a8a8a]">Facturación por tokens</div>
              </div>

              <div className="rounded-xl border border-[#262626] bg-[#141414] p-3.5">
                <span className="text-[10px] uppercase font-mono text-[#737373]">Rubro 2</span>
                <div className="mt-1 font-semibold text-xs text-[#ededed]">
                  {t.admin.sections.budget.bucket2}
                </div>
                <div className="mt-3 text-[11px] text-[#8a8a8a]">Coste por imagen liquidada</div>
              </div>

              <div className="rounded-xl border border-[#262626] bg-[#141414] p-3.5">
                <span className="text-[10px] uppercase font-mono text-[#737373]">Rubro 3</span>
                <div className="mt-1 font-semibold text-xs text-[#ededed]">
                  {t.admin.sections.budget.bucket3}
                </div>
                <div className="mt-3 text-[11px] text-[#8a8a8a]">Coste por segundo confirmado</div>
              </div>
            </div>
          </div>
        )}

        {/* SECCIÓN F: Registros */}
        {activeTab === "logs" && (
          <div data-testid="admin-section-logs" className="flex flex-col gap-6">
            <div>
              <h2 className="text-sm font-semibold text-[#ededed]">
                {t.admin.sections.logs.title}
              </h2>
              <p className="mt-0.5 text-xs text-[#8a8a8a]">
                {t.admin.sections.logs.desc}
              </p>
            </div>

            <PendingSectionBanner message={t.admin.sections.logs.pendingNotice} />
          </div>
        )}
      </div>

      {/* Modal para Crear / Editar Proveedor LLM */}
      <LLMProviderModal
        open={providerModalOpen}
        provider={editingProvider}
        templates={templates}
        loading={modalLoading}
        onClose={() => {
          setProviderModalOpen(false);
          setEditingProvider(null);
        }}
        onSave={handleCreateOrUpdateProvider}
      />
    </div>
  );
};

export default AdminPage;
